<template>
  <section :class="[style['slide-container']]">
    <figure :class="style.slide">
      <slider-image
        v-if="slide.media_content || showFallbackImage"
        :show-price-tag="needFetchPrice || !!slide.price"
        :slide="slide"
        :show-loading="showLoading"
      />
      <figcaption :class="[style.body]">
        <slider-title-block :slide="slide" />

        <div :class="style.content">
          <div v-if="price || slide.room_type_code" :class="style['price-content']">
            <div v-if="slide.room_type_code && showLoading" :class="style['spinner-dot']">
              <spinner-chat-dot />
            </div>
            <div v-else :class="style['price-container']">
              <div v-theme :class="style['price-title']">
                <span>{{ slide.price_title }}</span>
              </div>
              <div :class="style['price-title-arrow-header']"></div>
              <div :class="style['price-number']">
                <span>{{
                  `${price} ${$tc('for_night', numberOfNights)}
                  (${$t('including_taxes')})`
                }}</span>
              </div>
            </div>
          </div>

          <icon-list
            v-if="iconList.length > 0"
            :icon-list="iconList"
            :display-number="displayNumber"
          />
          <ul v-if="slide.detail_items[0]" v-theme-font :class="style['detail-list']">
            <li v-for="(detail, index) in slide.detail_items" :key="index">
              <img
                v-if="detail.icon"
                :key="index"
                :class="style.icon"
                :src="iconMapping[detail.icon]"
                alt=""
              />
              <a v-if="detail.url" :href="detail.url" target="_blank">{{
                detail.text || detail
              }}</a>
              <p v-else :class="style.text" v-html="formatChatMessage(detail.text || detail)"></p>
            </li>
          </ul>
        </div>
      </figcaption>
      <action-button-list
        v-if="slide.price || !slide.room_type_code"
        :slide="slide"
        :chat="chat"
        @sendReply="sendReply"
      />
    </figure>
  </section>
</template>

<script>
import { iconMapping } from 'javascript/Constants'
import { Chat } from 'src/apis'
import { formatPrice, formatChatMessage } from 'Utilities'
import style from './scss/_slider.scss'
import ActionButtonList from './ActionButtonList.vue'
import SliderImage from 'UtilityComponents/SliderImage.vue'
import IconList from 'UtilityComponents/IconList.vue'
import SliderTitleBlock from 'UtilityComponents/SliderTitleBlock.vue'
import SpinnerChatDot from 'UtilityComponents/SpinnerChatDot.vue'
import { parse, differenceInCalendarDays } from 'date-fns'
import { useAppStore } from 'Store/app'
import { useSettingsStore } from 'Store/settings'
import { useChatStore } from 'Store/chat'
import { useColorsStore } from 'Store/colors'

export default {
  components: {
    ActionButtonList,
    SliderImage,
    IconList,
    SliderTitleBlock,
    SpinnerChatDot
  },

  setup() {
    const appStore = useAppStore()
    const settingsStore = useSettingsStore()
    const chatStore = useChatStore()
    const colorsStore = useColorsStore()

    return { appStore, settingsStore, chatStore, colorsStore }
  },

  props: {
    chat: { type: Object, required: true },
    showFallbackImage: Boolean,
    sendReply: { type: Function, required: true },
    slide: { type: Object, required: true }
  },
  data() {
    return {
      showLoading: true,
      needFetchPrice:
        !!this.slide.room_type_code && this.appStore.latestMessageID - 3 < this.chat.id
    }
  },

  computed: {
    price() {
      return this.slide.price_with_tax || this.slide.priceWithTaxText || this.slide.priceWithTax
    },
    style: () => style,
    iconMapping: () => iconMapping,
    displayNumber() {
      return this.slide.adults && this.slide.adults > 5 ? this.slide.adults : 0
    },
    iconList() {
      const icons = []
      if (this.slide.smoking !== undefined) {
        icons.push(this.slide.smoking ? 'icon-smoking-yes' : 'icon-smoking-no')
      }
      if (this.slide.has_wifi) {
        icons.push('icon-wifi')
      }
      if (this.displayNumber) {
        icons.push('icon-person')
      } else {
        for (let i = 0; i < this.slide.adults; i++) {
          icons.push('icon-person')
        }
      }
      return icons
    },

    numberOfNights() {
      if (this.slide.checkin_date === this.slide.checkout_date || !this.slide.checkin_date) {
        return 0
      }
      const checkinDate = parse(this.slide.checkin_date, 'yyyy-MM-dd', new Date())
      const checkoutDate = parse(this.slide.checkout_date, 'yyyy-MM-dd', new Date())
      return differenceInCalendarDays(checkoutDate, checkinDate)
    }
  },
  mounted() {
    if (this.needFetchPrice) {
      Chat.getCheapestPrice(this.chatStore.chatId, {
        hotelID: this.settingsStore.getSettings.hotel_id,
        roomTypeCode: this.slide.room_type_code,
        checkinDate: this.slide.checkin_date,
        checkoutDate: this.slide.checkout_date,
        onlyCheckDates: this.slide.only_check_dates
      })
        .then((json) => {
          this.showLoading = false
          if (json.data && json.data.price) {
            this.slide.priceText = `${json.data.price}~`
            this.slide.price = formatPrice(json.data.price)
            this.slide.priceWithTaxText = `${json.data.price_with_tax}~`
            this.slide.priceWithTax = formatPrice(json.data.price_with_tax)
          }
          this.$emit('increaseApiCount')
        })
        .catch(() => {
          this.$emit('increaseApiCount')
        })
    }
  },
  methods: {
    formatChatMessage
  }
}
</script>
